import React, { useState, useEffect } from 'react';
import './styles/hero.css';
import Prisudregner from './prisudregner';
import CTAKnap, { CTAIkoner } from './ctaknap';

const Hero = ({ 
  title, 
  subtitle, 
  description, 
  showCTA = false, 
  ctaText, 
  ctaLink, 
  variant = "default",
  customTitle = null
} = {}) => {
  const [showNotification, setShowNotification] = useState(true);

  useEffect(() => {
    // Listen for changes in notification visibility
    const handleStorageChange = () => {
      const notificationClosed = localStorage.getItem('notificationClosed');
      setShowNotification(!notificationClosed);
    };

    // Check initial state
    handleStorageChange();

    // Listen for storage changes (when notification is closed)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for changes within the same tab
    const interval = setInterval(handleStorageChange, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // LocalBusiness Schema for Google
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Parla Branding",
    "description": "Professionelle hjemmesider og webdesign for virksomheder i hele Danmark",
    "url": "https://www.parlabranding.dk",
    "telephone": "+45-36-17-00-00",
    "priceRange": "10000-75000 DKK",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "DK",
      "addressRegion": "Danmark"
    },
    "openingHours": ["Mo-Fr 08:00-20:00", "Sa-Su 10:00-16:00"],
    "paymentAccepted": "Invoice, Credit Card, Bank Transfer",
    "currenciesAccepted": "DKK",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "13",
      "bestRating": "5",
      "worstRating": "1"
    },
    "sameAs": [
      "https://www.facebook.com/ParlaBranding/",
      "https://dk.linkedin.com/company/parlabranding",
      "https://www.instagram.com/parlabranding.dk"
    ]
  };

  // Standard indhold for default variant
  const defaultContent = {
    title: "Professionelle hjemmesider for danske virksomheder",
    subtitle: "Vi skaber skræddersyede løsninger i samarbejde med din virksomhed og jeres behov.",
    showPrisudregner: false,
    showActions: true
  };

  // Brug props eller default content
  const content = {
    title: title || defaultContent.title,
    subtitle: subtitle || description || defaultContent.subtitle,
    showPrisudregner: variant === "default" && defaultContent.showPrisudregner,
    showActions: variant === "default" ? defaultContent.showActions : showCTA
  };

  return (
    <section className={`hero-section hero-section--${variant} ${!showNotification ? 'hero-section--no-notification' : ''}`}>
      {/* LocalBusiness Schema - kun på default variant */}
      {variant === "default" && (
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      )}
      
      <div className="hero-section__background">
        <div className="hero-section__overlay"></div>
      </div>
      
      <div className="container">
        <div className="hero-section__content">
          <div className="hero-section__text">
            {customTitle ? (
              customTitle
            ) : (
              <h1 className="hero-section__title">
                {variant === "default" ? (
                  <>Hjemmesider med fokus på <span className="hero-section__highlight">dine kunder</span></>
                ) : variant === "cases" ? (
                  <>Vores <span className="hero-section__highlight">Cases</span></>
                ) : (
                  content.title
                )}
              </h1>
            )}
            
            <p className="hero-section__subtitle">
              {content.subtitle}
            </p>
            
            {content.showActions && (
              <div className="hero-section__actions">
                {variant === "default" ? (
                  <>
                    <CTAKnap 
                      variant="cta" 
                      size="large"
                      href="/priser#prisudregner"
                      icon={<CTAIkoner.ArrowRight />}
                      ariaLabel="Beregn din pris nu"
                    >
                      Beregn din pris nu
                    </CTAKnap>
                    
                    <CTAKnap 
                      variant="outline" 
                      size="large"
                      href="/tjenester"
                      icon={<CTAIkoner.Search />}
                      ariaLabel="Se vores tjenester"
                    >
                      Se vores tjenester
                    </CTAKnap>
                  </>
                ) : showCTA && (
                  <CTAKnap 
                    variant="primary" 
                    size="large"
                    href={ctaLink}
                    icon={<CTAIkoner.ArrowRight />}
                    ariaLabel={ctaText}
                  >
                    {ctaText}
                  </CTAKnap>
                )}
              </div>
            )}
            
            {variant === "default" && (
              <div className="hero-section__trust">
                <p className="hero-section__trust-text">
                  <span className="hero-section__trust-icon">✓</span>
                  Ingen binding • Gratis konsultation • Resultater garanteret
                </p>
              </div>
            )}
          </div>
          
          {content.showPrisudregner && (
            <div id="prisudregner" className="hero-section__visual">
              <Prisudregner />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
